<script setup>
import { ref, computed } from 'vue';
import { Head } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import PremiumTabs from '@/Components/PremiumTabs.vue';
import { Icon } from '@iconify/vue';

// Subcomponentes de pestañas
import OrderTab from '@/Pages/Financiera/Order.vue';
import PaymentTab from '@/Pages/Financiera/Payment.vue';
import BankTab from '@/Pages/Financiera/Bank.vue';

const props = defineProps({
    courses: Array,
    bankAccounts: Array,
    students: Array,
    stats: Object,
    anioEscolar: Number,
    permissions: {
        type: Object,
        default: () => ({})
    }
});

const currentBankAccounts = ref([...(props.bankAccounts || [])]);
const paymentTabRef = ref(null);

const tabsMenu = computed(() => {
    const list = [];
    if (props.permissions?.orders_read !== false) {
        list.push({ id: 0, label: 'Órdenes de Pago', icon: 'tabler:receipt' });
    }
    if (props.permissions?.payments_read !== false) {
        list.push({ id: 1, label: 'Pagos y Sustentos', icon: 'tabler:cash' });
    }
    if (props.permissions?.banks_read !== false) {
        list.push({ id: 2, label: 'Bancos y Conciliación', icon: 'tabler:building-bank' });
    }
    return list;
});

const activeTab = ref(
    props.permissions?.orders_read !== false ? 0 :
    (props.permissions?.payments_read !== false ? 1 : 2)
);

const handlePayOrder = (data) => {
    activeTab.value = 1;
    setTimeout(() => {
        if (paymentTabRef.value?.openCreatePaymentModal) {
            paymentTabRef.value.openCreatePaymentModal(data);
        }
    }, 100);
};

const handleAccountsUpdated = (newAccounts) => {
    currentBankAccounts.value = newAccounts;
};
</script>

<template>
    <Head title="Financiera - Cursos de Formación" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                title="Financiera - Cursos de Formación"
                subtitle="Gestión de órdenes de pago, recaudación, sustentos de pago y conciliación bancaria"
                icon="tabler:wallet"
                color="indigo"
            >
                <template #bottom>
                    <PremiumTabs
                        v-model="activeTab"
                        :tabs="tabsMenu"
                    />
                </template>
            </PremiumPageHeader>
        </template>

        <div class="space-y-6 pb-12">
            <!-- Tarjetas de Resumen KPI -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Total Emitido -->
                <div class="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p class="text-xs font-semibold text-slate-400">Total Facturado / Emitido</p>
                        <h3 class="text-2xl font-black text-slate-800 dark:text-white mt-1">
                            S/ {{ Number(stats.total_billed || 0).toFixed(2) }}
                        </h3>
                        <p class="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold mt-0.5">
                            {{ stats.total_orders }} órdenes en total
                        </p>
                    </div>
                    <div class="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                        <Icon icon="tabler:file-invoice" class="w-7 h-7" />
                    </div>
                </div>

                <!-- Total Recaudado (Cobrado) -->
                <div class="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p class="text-xs font-semibold text-slate-400">Total Recaudado (Cobrado)</p>
                        <h3 class="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                            S/ {{ Number(stats.total_paid || 0).toFixed(2) }}
                        </h3>
                        <p class="text-[11px] text-emerald-600 font-semibold mt-0.5">
                            {{ stats.paid_count }} órdenes saldadas
                        </p>
                    </div>
                    <div class="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
                        <Icon icon="tabler:cash" class="w-7 h-7" />
                    </div>
                </div>

                <!-- Pendiente por Cobrar -->
                <div class="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p class="text-xs font-semibold text-slate-400">Pendiente por Cobrar</p>
                        <h3 class="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">
                            S/ {{ Number(stats.total_balance || 0).toFixed(2) }}
                        </h3>
                        <p class="text-[11px] text-rose-500 font-semibold mt-0.5">
                            {{ stats.pending_count + stats.partial_count }} órdenes pendientes/parciales
                        </p>
                    </div>
                    <div class="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400">
                        <Icon icon="tabler:clock-alert" class="w-7 h-7" />
                    </div>
                </div>

                <!-- Pagos Registrados / Conciliación -->
                <div class="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p class="text-xs font-semibold text-slate-400">Pagos Registrados</p>
                        <h3 class="text-2xl font-black text-slate-800 dark:text-white mt-1">
                            S/ {{ Number(stats.total_payments_amount || 0).toFixed(2) }}
                        </h3>
                        <p class="text-[11px] text-slate-500 font-semibold mt-0.5">
                            {{ stats.total_payments_count }} abonos | {{ stats.unconciliated_bank_count }} por conciliar
                        </p>
                    </div>
                    <div class="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
                        <Icon icon="tabler:building-bank" class="w-7 h-7" />
                    </div>
                </div>
            </div>

            <!-- Contenido de las Pestañas -->
            <div>
                <!-- Pestaña 0: Órdenes de Pago -->
                <div v-if="permissions?.orders_read !== false" v-show="activeTab === 0">
                    <OrderTab
                        :courses="courses"
                        :students="students"
                        :bankAccounts="currentBankAccounts"
                        :anioEscolar="anioEscolar"
                        :permissions="permissions"
                        @open-payment-modal="handlePayOrder"
                    />
                </div>

                <!-- Pestaña 1: Pagos y Sustentos -->
                <div v-if="permissions?.payments_read !== false" v-show="activeTab === 1">
                    <PaymentTab
                        ref="paymentTabRef"
                        :courses="courses"
                        :students="students"
                        :bankAccounts="currentBankAccounts"
                        :anioEscolar="anioEscolar"
                        :permissions="permissions"
                    />
                </div>

                <!-- Pestaña 2: Bancos y Conciliación -->
                <div v-if="permissions?.banks_read !== false" v-show="activeTab === 2">
                    <BankTab
                        v-if="activeTab === 2"
                        :bankAccounts="currentBankAccounts"
                        :anioEscolar="anioEscolar"
                        :permissions="permissions"
                        @accounts-updated="handleAccountsUpdated"
                    />
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
